import { GridColDef } from "@mui/x-data-grid-pro";
import { Button, IconButton, Tooltip } from "@mui/material";

import ItemStatus from "../../../../../../common/components/item_status";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import dayjs from "dayjs";

/**
 * GenerateTreinamentosColumnsDefinition
 */

type Props = {
  onAvaliarTreinamento: (idTreinamento: number) => void,
  onExportar: (idTreinamento: number) => void,
  onCertificate: (idTreinamento: number) => void,
  onEditar: (idTreinamento: number) => void,
  onDeletar: (idTreinamento: number) => void,
};

export const GenerateTreinamentosColumnsDefinition = ({
  onAvaliarTreinamento,
  onExportar,
  onCertificate,
  onEditar,
  onDeletar,
}: Props): GridColDef[] => {
  const computeItemStatusTitle = (status: number) => {
    switch (status) {
      case 1: return "A";
      case 2: return "D";
      case 3: return "N";
      case 4: return "A";
      default:
        return "?";
    }
  };

  const computeItemStatusColor = (status: number) => {
    switch (status) {
      case 1: return "red";
      case 2: return "blue";
      case 3: return "yellow";
      case 4: return "green";
      default:
        return "blue";
    }
  };

  const computeItemStatusDescription = (status: number) => {
    switch (status) {
      case 1: return "Avaliação em Atraso";
      case 2: return "Dentro do Prazo";
      case 3: return "Não Realizado";
      case 4: return "Avaliados";
      default:
        return "?";
    }
  };

  const computeAvaliarTreinamentoClass = (avaliarTreinamento?: boolean): string => {
    if (avaliarTreinamento === true) {
      return '#35aa47';
    } else if (avaliarTreinamento === false) {
      return '#d84a38';
    } else {
      return "#ffb848";
    }
  }

  const computeEficazClass = (eficaz?: boolean): string => {
    switch (eficaz) {
      case true: return 'bg-[#45b6af] text-white py-[.1rem] px-[.4rem] font-normal';
      case false: return 'bg-[#f3565d] text-white py-[.1rem] px-[.4rem] font-normal';
      default: return 'bg-[#a6a6a6] text-white py-[.1rem] px-[.4rem] font-normal';
    }
  }

  const computeEficazLabel = (eficaz?: boolean): string => {
    switch (eficaz) {
      case true: return 'Sim';
      case false: return 'Não';
      default: return 'Não avaliado';
    }
  }

  const computeAssinadoClass = (assinado: boolean): string => {
    return assinado
      ? 'bg-[#45b6af] text-white py-[.1rem] px-[.4rem] font-normal'
      : 'bg-[#f3565d] text-white py-[.1rem] px-[.4rem] font-normal';
  }

  return [
    {
      field: "status",
      filterable: false,
      headerName: "Status",
      width: 55,
      sortable: false,
      align: 'center',
      renderCell: (params) => (
        <div className="w-full h-full flex justify-center items-center text-center text-xs">
          <ItemStatus
            title={computeItemStatusTitle(params.value)}
            color={computeItemStatusColor(params.value)}
            description={computeItemStatusDescription(params.value)} />
        </div>
      )
    },
    {
      field: "avaliarTreinamento",
      filterable: false,
      headerName: "Avaliação",
      width: 70,
      sortable: false,
      align: 'center',
      valueGetter: (value, row) => {
        return ({ value, id: row['idFuncionarioTreinamento'] as number })
      },
      renderCell: ({ value: { value, id, status } }) => (
        <div className="w-full h-full flex justify-center items-center text-center text-xs">
          <Tooltip title="Avaliar FuncionarioTreinamento">
            <Button
              onMouseDown={() => onAvaliarTreinamento(id)}
              sx={{ background: computeAvaliarTreinamentoClass(value), minWidth: '25px', maxWidth: '25px' }}>
              <DynamicIcon nameIcon="CheckBox" size={15} className="text-white" />
            </Button>
          </Tooltip>
        </div>
      ),
    },
    {
      field: "treinamento",
      filterable: false,
      headerName: "Treinamento",
      flex: 1,
      minWidth: 80,
      sortable: false,
      headerAlign: 'center',
      renderCell: ({ value }) => (
        <div className="w-full h-full flex justify-center items-center text-center text-xs">
          {value}
        </div>
      ),
    },
    {
      field: "versao",
      filterable: false,
      headerName: "Versão",
      width: 60,
      sortable: false,
      renderCell: ({ value }) => (
        <div className="w-full h-full flex justify-center items-center text-center text-xs">
          {value ?? "-"}
        </div>
      ),
    },
    {
      field: "instrutor",
      filterable: false,
      headerName: "Instrutor",
      width: 230,
      minWidth: 100,
      sortable: false,
      headerAlign: 'center',
      renderCell: ({ value }) => (
        <div className="w-full h-full flex justify-center items-center text-center text-xs">
          {value}
        </div>
      ),
    },
    {
      field: "dataInicio",
      filterable: false,
      headerName: "Data",
      width: 70,
      minWidth: 70,
      sortable: false,
      renderCell: ({ value }) => (
        <div className="w-full h-full flex justify-center items-center text-center text-xs">
          {value && dayjs(value).format("DD/MM/YYYY")}
        </div>
      ),
    },
    {
      field: "prazo",
      filterable: false,
      headerName: "Prazo",
      width: 50,
      minWidth: 50,
      sortable: false,
      renderCell: ({ value }) => (
        <div className="w-full h-full flex justify-center items-center text-center text-xs">
          {value}
        </div>
      ),
    },
    {
      field: "dataPrevAvaliacao",
      filterable: false,
      headerName: "Data Prev. Avaliação",
      sortable: false,
      width: 70,
      minWidth: 70,
      renderCell: ({ value }) => (
        <div className="w-full h-full flex justify-center items-center text-center text-xs">
          {value && dayjs(value).format("DD/MM/YYYY")}
        </div>
      ),
    },
    {
      field: "dataAvaliacao",
      filterable: false,
      headerName: "Data Avaliação",
      sortable: false,
      renderCell: ({ value }) => (
        <div className="w-full h-full flex justify-center items-center text-center text-xs">
          {value && dayjs(value).format("DD/MM/YYYY")}
        </div>
      ),
    },
    {
      field: "avaliador",
      filterable: false,
      headerName: "Avaliador",
      width: 150,
      minWidth: 150,
      sortable: false,
      headerAlign: 'center',
      renderCell: ({ value }) => (
        <div className="w-full h-full flex justify-center items-center text-center text-xs">
          {value}
        </div>
      ),
    },
    {
      field: "eficaz",
      filterable: false,
      headerName: "Eficaz?",
      width: 110,
      sortable: false,
      renderCell: ({ value }) => (
        <div className="w-full h-full flex justify-center items-center text-center text-xs">
          <div>
            <span className={computeEficazClass(value)}>
              {computeEficazLabel(value)}
            </span>
          </div>
        </div>
      ),
    },
    {
      field: "assinado",
      filterable: false,
      headerName: "Assinado",
      width: 75,
      sortable: false,
      renderCell: ({ value }) => (
        <div className="w-full h-full flex justify-center items-center text-center text-xs">
          <div>
            <span className={computeAssinadoClass(value)}>
              {value === true ? "Sim" : "Não"}
            </span>
          </div>
        </div>
      ),
    },
    {
      field: "exportar",
      filterable: false,
      headerName: "Modelo Lista",
      width: 75,
      sortable: false,
      valueGetter: (_, row) => { return row['idFuncionarioTreinamento'] as number; },
      renderCell: ({ value }) => {
        return (
          <div className="w-full h-full flex justify-center items-center text-center text-xs">
            <Button
              onMouseDown={() => onExportar(value)}
              sx={{
                fontSize: 9,
                paddingX: '.3rem',
                paddingY: 0,
                background: '#e6e6e6',
                borderLeft: '.2rem solid #45b6af',
                borderRadius: 0,
                color: 'black',
                fontWeight: 'normal',
              }}>
              Exportar
            </Button>
          </div>
        );
      },
    },
    {
      field: "certificado",
      filterable: false,
      headerName: "Modelo Certificado",
      width: 90,
      sortable: false,
      valueGetter: (_, row) => { return row['idFuncionarioTreinamento'] as number; },
      renderCell: ({ value }) => {
        return (
          <div className="w-full h-full flex justify-center items-center text-center text-xs">
            <Button
              onMouseDown={() => onCertificate(value)}
              sx={{
                fontSize: 9,
                paddingX: '.3rem',
                paddingY: 0,
                background: '#e6e6e6',
                borderLeft: '.2rem solid #45b6af',
                borderRadius: 0,
                color: 'black',
                fontWeight: 'normal',
              }}>
              Certificado
            </Button>
          </div>
        );
      },
    },
    {
      field: "acoes",
      filterable: false,
      headerName: "Ações",
      sortable: false,
      valueGetter: (_, row) => row['idFuncionarioTreinamento'] as number,
      renderCell: ({ value }) => {
        return (
          <div className="flex items-center justify-center w-full h-full gap-2">
            <IconButton onClick={() => onEditar(value)}>
              <DynamicIcon nameIcon="EditRounded" size={25} className="dark:text-white text-primary-green" />
            </IconButton>

            <IconButton onClick={() => onDeletar(value)}>
              <DynamicIcon nameIcon="DeleteForeverRounded" size={25} className="text-red-500" />
            </IconButton>
          </div>
        );
      }
    },
  ];
}